// =============================================================================
// domain/freeTier.ts — how much of the app a free account gets.
// -----------------------------------------------------------------------------
// Relocated from the old client's three cap constants and their two helpers
// (app/index.html:40197-40274). #65, and the other half of #55: that issue put a
// reader on the Pro tab gate, this is everything the free tier does BESIDES
// locking whole tabs.
//
// ── ⚠ WHAT IS AND IS NOT CAPPED ──
//
// Three registries, and only these three. There is **no trade-count cap, no
// date-range cap and no export cap** in the old client, and inventing one here
// would be a product change wearing a parity port's clothes.
//
// ── 🚨 3 AND 3 ARE DELIBERATELY EQUAL, AND THE COMMENT IS WORTH KEEPING ──
//
// The old client's own words: patterns and exit models interact — the pattern
// form requires an exit model — "so capping one and not the other just moves the
// wall". Its free-tier suite asserts the two limits are EQUAL to each other, not
// merely that each is 3.
//
// ── ⚠ ARCHIVED AND RETIRED ROWS COUNT ──
//
// The old client counts everything except demo rows. An archived account and a
// retired pattern both still consume the allowance, and its suite pins that
// explicitly. Archiving is de-clutter, not deletion, so a cap that ignored it
// would be a free unlimited tier for anyone who archives.
//
// ⚠ THE DEMO CARVE-OUT IS NOT PORTED, because this client has no demo mode at
// all — see `ui/tabs/GetStarted.tsx`. If a demo ever lands, its rows must stop
// counting here BEFORE it ships: the old client shipped without that and a new
// user who clicked "Demo Account" was told they were over a limit they had never
// touched, then navigated off the demo.
//
// ── 🚨 A COUNT WE DO NOT HAVE MUST NOT BLOCK ──
//
// `count: null` means the read has not landed or has failed. It ALLOWS, the same
// direction `hasProAccess` fails, and for the same reason: a free user creating
// a second account after a failed read is a bounded leak, while a paying-or-not
// user blocked from creating their FIRST account by a network blip has no way
// forward and no way to know why.
// =============================================================================

export const FREE_ACCOUNT_LIMIT = 1;

// ⚠ These two are equal on purpose. See the header.
export const FREE_PATTERN_LIMIT = 3;
export const FREE_EXIT_MODEL_LIMIT = 3;

/** The three capped registries. A new key here is a type error at every reader. */
export type FreeTierRegistry = 'accounts' | 'patterns' | 'exitModels';

export const FREE_TIER_LIMITS: Record<FreeTierRegistry, number> = {
  accounts: FREE_ACCOUNT_LIMIT,
  patterns: FREE_PATTERN_LIMIT,
  exitModels: FREE_EXIT_MODEL_LIMIT,
};

/**
 * How the refusal names the thing.
 *
 * ⚠ Built from the LIMIT, never a literal. The old client's suite asserts its
 * pricing matrix interpolates these too, because "a hardcoded number here
 * silently lies the day the cap changes".
 */
const SUBJECT: Record<FreeTierRegistry, (limit: number) => string> = {
  // The old client's exact phrasing for this one — it reads as a sentence, not
  // as a count, because the limit is one.
  accounts: () => 'Adding more than one account',
  patterns: (n) => `More than ${n} patterns`,
  exitModels: (n) => `More than ${n} exit models`,
};

/**
 * Is this creation refused, and what does the user get told?
 *
 * Returns `null` when the create may proceed, or the sentence to show when it
 * may not.
 *
 * ── ⚠ WHY THE OLD CLIENT'S *SECOND* SENTENCE, NOT ITS FIRST ──
 *
 * Over there a blocked create raises a toast and NAVIGATES the user to My
 * Account — "… is a Pro feature — upgrade to unlock it." Its wizard steps pass
 * `{ nav: false }` and get a different sentence instead, because navigating
 * would tear down a half-finished flow.
 *
 * This client is in the `nav: false` case everywhere: it has no toast system at
 * all (a deliberate choice — surfaces report inline), and every one of these
 * refusals happens inside a modal the user opened. Yanking them to another tab
 * from inside a dialog would be worse here than it is there. So the sentence
 * that tells them WHERE to go is the right one, and it is still the old
 * client's own copy rather than something invented.
 */
export function freeTierBlocks(
  args: { pro: boolean; registry: FreeTierRegistry; count: number | null },
): string | null {
  // ⚠ FIRST, exactly as the old client's helpers do. While `PAID_LAUNCH` is
  // false everybody is Pro, so the whole of this file is inert.
  if (args.pro) return null;
  // See the header: a count we do not have cannot be used to refuse.
  if (args.count === null) return null;

  const limit = FREE_TIER_LIMITS[args.registry];
  if (args.count < limit) return null;

  // ⚠ Blocks AT the limit, not over it: at 3 patterns the next one is refused.
  return `${SUBJECT[args.registry](limit)} — upgrade in My Account to unlock.`;
}
